import api from './api';

/**
 * Signature Service
 * API client for digital signature operations
 */
class SignatureService {
  async createSignature(signatureData) {
    const response = await api.post('/signatures/create', signatureData);
    return response.data;
  }

  async getSignature(signatureId) {
    const response = await api.get(`/signatures/${signatureId}`);
    return response.data;
  }

  async getDocumentSignatures(documentType, documentId) {
    const response = await api.get(`/signatures/document/${documentType}/${documentId}`);
    return response.data;
  }

  async verifySignature(signatureId, currentDocumentData = null) {
    const response = await api.post(`/signatures/${signatureId}/verify`, { currentDocumentData });
    return response.data;
  }

  async downloadSignedPDF(signatureId) {
    const response = await api.get(`/signatures/${signatureId}/pdf`, { responseType: 'blob' });
    return response.data;
  }

  async triggerPDFDownload(signatureId, filename = 'signed-document.pdf') {
    try {
      const blob = await this.downloadSignedPDF(signatureId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw new Error('Failed to download signed document');
    }
  }

  async getAuditTrail(signatureId) {
    const response = await api.get(`/signatures/${signatureId}/audit-trail`);
    return response.data;
  }

  async previewDocument(documentType, documentId) {
    const response = await api.post('/signatures/preview-pdf', { documentType, documentId });
    return response.data;
  }

  getConsentText(documentType) {
    const documentLabel = documentType === 'bid' ? 'Bid' : 'Milestone Report';
    return `By signing this ${documentLabel} electronically, I agree that my electronic signature is the legal equivalent of my manual signature on this document. I consent to be legally bound by this ${documentLabel}'s terms, conditions, and all data provided herein. I understand that I have the right to request a paper copy of this document.

This electronic signature is governed by the Electronic Signatures in Global and National Commerce Act (ESIGN Act), the Uniform Electronic Transactions Act (UETA), and applicable state laws. The signature is binding and enforceable.

I acknowledge that:
1. I have reviewed the complete ${documentLabel} document
2. I agree to all terms and conditions stated in this ${documentLabel}
3. My electronic signature has the same legal effect as a handwritten signature
4. I am authorized to sign this ${documentLabel}
5. This signature creates a binding legal agreement

Signed on: ${new Date().toISOString()}`;
  }

  getESignatureDisclosure() {
    return `Electronic Signature Disclosure (Version 1.0)

By providing your electronic signature, you consent to sign electronically and agree that electronic signatures, contracts, and records are valid and enforceable. You will receive a copy of this signed document for your records.

System Requirements:
- A device with internet access
- A web browser (Chrome, Firefox, Safari, Edge)
- Email access to receive notifications

Right to Request Paper Copies:
You have the right to request a paper copy of any electronically signed document. To request a paper copy, please contact info@eqaya.com.

Withdrawal of Consent:
You may withdraw your consent to electronic signatures at any time by contacting info@eqaya.com. Withdrawal of consent will not affect the validity of signatures provided before withdrawal.

Security:
Your signature is protected with industry-standard encryption and security measures. All signatures are timestamped and include audit trail information for verification purposes.`;
  }
}

export default new SignatureService();
